import styled from '@emotion/styled';
import { utSectionStyles, utTokens } from './sharedStyles';

/**
 * 원본 UT_Comparison_v1_3.html의 전체 CSS를 Emotion styled 루트로 스코핑.
 * (내부 프로젝트 통합 시 전역 스타일 오염 방지 — 외부 개발 가이드 §3)
 * - body → Root 자체 스타일
 * - header → .page-header
 * - 나머지 클래스/태그 셀렉터는 Root 하위 자손 셀렉터로 동일 적용
 */
/* LGES Design Guideline v1.3.0 토큰 + PI 개발 가이드 구현 표준 — 상세: D:\AI\LGES_STYLE_GUIDE.md */
export const Root = styled.div`
  /* 디자인 토큰·섹션 머리 스타일은 전기 탭과 공유한다 (sharedStyles.ts) */
  ${utTokens}
  ${utSectionStyles}

  &,
  & * {
    box-sizing: border-box;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  ul,
  ol,
  dl,
  dd,
  figure,
  blockquote,
  fieldset {
    margin: 0;
    padding: 0;
  }
  button:not([class*='Mui']) {
    margin: 0;
    padding: 0;
  }
  input,
  select,
  textarea {
    margin: 0;
  }

  input[type='checkbox'],
  input[type='radio'] {
    accent-color: #554596; /* 레거시 체크박스/라디오 (Secondary-500) */
  }

  font-family: 'Malgun Gothic', 'Segoe UI', 'MS YaHei', sans-serif; /* PI 표준 폰트 스택 */
  /* 배경은 깔지 않는다 — 전기 탭과 톤을 맞추려고 뺐다(예전엔 var(--bg) Grey-50) */
  color: var(--text);
  font-size: 13px;
  line-height: 1.5;
  min-height: 100%; /* AppShell 탭패널 내부에서 렌더링됨 (페이지 헤더는 셸이 담당) */

  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 8px 16px 24px; /* 셸 타이틀 바(16px 좌우)와 정렬 */
  }

  .upload-card {
    background: var(--surface);
    border: 2px dashed var(--border);
    border-radius: var(--radius);
    padding: 40px 32px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
  }
  .upload-card:hover,
  .upload-card.drag-over {
    border-color: var(--primary);
    background: #ecfcf8; /* Primary-50 */
  }
  .upload-card svg {
    color: var(--text-light);
    margin-bottom: 12px;
  }
  .upload-card h2 {
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 6px;
  }
  .upload-card p {
    font-size: 12px;
    color: var(--text-muted);
  }
  .hidden-file-input {
    display: none;
  }

  .file-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #dcfbea; /* Confirmed-100 */
    color: var(--success);
    border: 1px solid #56d8aa; /* Confirmed-300 */
    border-radius: 20px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    margin-top: 12px;
  }

  .controls-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin: 20px 0;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 32px; /* PI 버튼 md 규격 */
    padding: 6px 16px;
    border-radius: var(--radius);
    border: none;
    font-size: 13px;
    font-weight: 700; /* PI 버튼 표준 웨이트 */
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
    font-family: inherit;
  }
  .btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .btn-primary {
    background: var(--btn-primary); /* 레거시 primaryBtn */
    color: #fff;
  }
  .btn-primary:not(:disabled):hover {
    background: var(--btn-primary-hover);
  }
  .btn-primary:not(:disabled):active {
    background: var(--btn-primary-active);
  }
  .btn-success {
    background: var(--success);
    color: #fff;
  }
  .btn-success:not(:disabled):hover {
    background: #01463a; /* Confirmed-700 */
  }
  .btn-accent {
    background: var(--accent);
    color: #fff;
  }
  .btn-accent:not(:disabled):hover {
    background: #403281; /* Secondary-600 */
  }
  .btn-outline {
    background: var(--surface);
    color: var(--primary);
    border: 1.5px solid var(--primary);
  }
  .btn-outline:not(:disabled):hover {
    background: var(--primary);
    color: #fff;
  }

  .sheet-info {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin-bottom: 20px;
  }
  .sheet-chip {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .sheet-chip .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .dot-v1 {
    background: #5e5fb7; /* Support-Deepblue-400 */
  }
  .dot-v2 {
    background: #ff9322; /* Warning-Major-500 */
  }
  .dot-v3 {
    background: #2d9bb2; /* Primary */
  }
  .sheet-chip strong {
    font-size: 12px;
    color: var(--text-muted);
    display: block;
  }
  .sheet-chip span {
    font-size: 13px;
    font-weight: 700;
  }

  .ut-tabs {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }
  .ut-tab {
    padding: 5px 12px;
    border-radius: 20px;
    border: 1.5px solid var(--border);
    background: var(--surface);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    color: var(--text-muted);
  }
  .ut-tab.active {
    background: var(--primary);
    color: #fff;
    border-color: var(--primary);
  }
  .ut-tab:hover:not(.active) {
    border-color: var(--primary);
    color: var(--primary);
  }

  /* ── 분석 테이블 (헤더 고정 + UT 타이틀 고정) ── */
  .table-outer {
    background: var(--surface);
    border-radius: var(--radius);
    border: 1px solid var(--border);
    box-shadow: var(--shadow);
    margin-bottom: 20px;
    max-height: 480px;
    overflow: auto;
    position: relative;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    min-width: 900px;
  }

  /* 헤더 전체 행 고정 — LGES Table-Bg-Header(Grey-100) + 다크 텍스트 */
  thead th {
    background: #f1f4f3;
    color: var(--text);
    padding: 9px 12px;
    font-size: 11px;
    font-weight: 700;
    text-align: center;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 4;
    border-bottom: 1px solid var(--border); /* Table-Border-Header */
  }
  thead th.group-header {
    background: #ebeeed; /* Grey-200 */
    font-size: 11px;
    top: 0;
  }
  /* 2번째 헤더 행 (사용량/Q'ty 소헤더) */
  thead tr:nth-of-type(2) th {
    top: 32px; /* 1행 높이만큼 */
    z-index: 3;
  }

  thead th:first-of-type {
    text-align: left;
  }

  tbody tr:nth-of-type(even) {
    background: var(--surface2);
  }
  tbody tr:hover {
    background: var(--selected-bg); /* 레거시 그리드 선택/포커스 (Secondary-50) */
  }

  td {
    padding: 7px 8px; /* 레거시 셀 패딩 */
    border-bottom: 1px solid #f1f4f3; /* Table-Border-Cell (Grey-100) */
    font-size: 12px;
    text-align: right;
    white-space: nowrap;
  }
  td:nth-of-type(1),
  td:nth-of-type(2),
  td:nth-of-type(3),
  td:nth-of-type(4),
  td:nth-of-type(5) {
    text-align: left;
  }

  /* 첫 5열(공정/설비군/세부공정/Machine/라인) 고정 */
  thead th:nth-of-type(1),
  thead th:nth-of-type(2),
  thead th:nth-of-type(3),
  thead th:nth-of-type(4),
  thead th:nth-of-type(5) {
    position: sticky;
    left: 0;
    z-index: 6;
    background: #f1f4f3;
  }
  thead tr:nth-of-type(2) th:nth-of-type(1),
  thead tr:nth-of-type(2) th:nth-of-type(2),
  thead tr:nth-of-type(2) th:nth-of-type(3),
  thead tr:nth-of-type(2) th:nth-of-type(4),
  thead tr:nth-of-type(2) th:nth-of-type(5) {
    z-index: 5;
  }
  tbody td:nth-of-type(1) {
    position: sticky;
    left: 0;
    background: #f7f9f8; /* Grey-50 */
    z-index: 2;
  }
  tbody td:nth-of-type(2) {
    position: sticky;
    left: 60px;
    background: #f7f9f8; /* Grey-50 */
    z-index: 2;
  }
  tbody td:nth-of-type(3) {
    position: sticky;
    left: 130px;
    background: #f7f9f8; /* Grey-50 */
    z-index: 2;
  }
  tbody td:nth-of-type(4) {
    position: sticky;
    left: 200px;
    background: #f7f9f8; /* Grey-50 */
    z-index: 2;
  }
  tbody td:nth-of-type(5) {
    position: sticky;
    left: 280px;
    background: #f7f9f8; /* Grey-50 */
    z-index: 2;
  }
  tbody tr:hover td:nth-of-type(1),
  tbody tr:hover td:nth-of-type(2),
  tbody tr:hover td:nth-of-type(3),
  tbody tr:hover td:nth-of-type(4),
  tbody tr:hover td:nth-of-type(5) {
    background: var(--selected-bg);
  }

  .diff-pos {
    color: var(--danger);
    font-weight: 700;
  }
  .diff-neg {
    color: var(--success);
    font-weight: 700;
  }
  .diff-zero {
    color: var(--text-light);
  }

  .highlight-row {
    background: #fff7eb !important; /* Warning-Major-50 */
  }
  .highlight-row:hover {
    background: #feefd6 !important; /* Warning-Major-100 */
  }
  .highlight-row td:nth-of-type(1),
  .highlight-row td:nth-of-type(2),
  .highlight-row td:nth-of-type(3),
  .highlight-row td:nth-of-type(4),
  .highlight-row td:nth-of-type(5) {
    background: #fff7eb !important;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;
    margin-bottom: 20px;
  }
  .summary-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 12px 14px;
    text-align: center;
  }
  .summary-card .label {
    font-size: 11px;
    color: var(--text-muted);
    margin-bottom: 4px;
  }
  .summary-card .value {
    font-size: 20px;
    font-weight: 800;
    color: var(--primary);
  }
  .summary-card .unit {
    font-size: 10px;
    color: var(--text-light);
    margin-top: 2px;
  }
  .summary-card.danger .value {
    color: var(--danger);
  }
  .summary-card.success .value {
    color: var(--success);
  }

  /* ── 전체 Summary 표 ── */
  /*
    헤더·셀·줄무늬·hover는 **따로 지정하지 않는다** — 위의 사이트 기본 규칙(thead th / td)을
    그대로 물려받아 세부 Summary 표와 같은 모양이 된다. 여기서는 고정열만 손본다.

    ⚠ 이 표의 고정열은 **버전 열 하나뿐**이다. 그런데 분석 표용 고정열 규칙
      "thead th:nth-of-type(1~5)" / "tbody td:nth-of-type(1~5)"가 Root 안의 모든 표에
      걸려서, 그대로 두면 UT 데이터 칸(2~5번째)까지 left:60/130/200/280px로 왼쪽에
      붙어 서로 겹쳐 보인다. 그래서 2번째 칸부터 되돌린다.

    ⚠ 셀렉터에 타입(thead/tbody/th/td)을 함께 쓴다. 위 고정열 규칙의 특이도가 (0,1,2)라
      클래스만 얹은 ".total-summary-table th"(0,1,1)로는 못 이긴다.
  */
  .total-summary-table thead th:nth-of-type(n + 2) {
    position: static;
    left: auto;
    z-index: auto;
  }
  .total-summary-table tbody td:nth-of-type(n + 2) {
    position: static;
    left: auto;
    z-index: auto;
    /* 셀 배경을 비워 행 단위 줄무늬·hover가 그대로 비치게 한다 */
    background: transparent;
    text-align: right;
  }
  /* 차이 행 — 세부 Summary의 합계 행과 같은 강조(Confirmed-100). 고정열 칸까지 덮는다 */
  .total-summary-table tbody tr.diff-row td {
    font-weight: 700;
    background: #d9fcf4;
  }
  .tor-wrap {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    padding: 16px 18px;
    margin-bottom: 20px;
  }
  .tor-legend {
    display: flex;
    gap: 16px;
    align-items: center;
    margin-bottom: 14px;
    font-size: 12px;
    color: var(--text-muted);
    flex-wrap: wrap;
  }
  .tor-legend .lg {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .tor-legend .ln-legend {
    display: inline-block;
    color: var(--text-light);
    font-size: 10px;
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 0 4px;
  }
  .tor-legend .mn-legend {
    display: inline-block;
    background: #e0f2fe;
    color: #0369a1;
    font-size: 10px;
    border-radius: 3px;
    padding: 0 5px;
    font-weight: 600;
  }
  .tor-legend .sw {
    width: 13px;
    height: 13px;
    border-radius: 3px;
  }
  .tor-legend .sw.inc {
    background: linear-gradient(90deg, #f59e0b, #dc2626);
  }
  .tor-legend .sw.dec {
    background: linear-gradient(270deg, #16a34a, #0ea5e9);
  }
  .tor-chart {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .tor-row {
    display: grid;
    grid-template-columns: 300px 1fr 104px;
    align-items: center;
    gap: 10px;
    height: 24px;
    font-size: 11.5px;
  }
  .tor-row:hover {
    background: var(--selected-bg);
  }
  .tor-name {
    text-align: right;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .tor-name .ln {
    color: var(--text-light);
    font-size: 10px;
    margin-left: 4px;
  }
  .tor-name .eq {
    color: var(--text-muted);
    font-weight: 600;
  }
  .tor-name .mn {
    display: inline-block;
    background: #e0f2fe;
    color: #0369a1;
    border-radius: 3px;
    padding: 0 5px;
    font-size: 10px;
    font-weight: 600;
    margin: 0 2px;
  }
  .tor-track {
    position: relative;
    height: 15px;
  }
  /* 가운데 0선 */
  .tor-track::before {
    content: '';
    position: absolute;
    left: 50%;
    top: -5px;
    bottom: -5px;
    width: 1px;
    background: var(--border);
  }
  .tor-fill {
    position: absolute;
    top: 0;
    height: 100%;
  }
  .tor-fill.pos {
    left: 50%;
    background: linear-gradient(90deg, #f59e0b, #dc2626);
    border-radius: 0 3px 3px 0;
  }
  .tor-fill.neg {
    right: 50%;
    background: linear-gradient(270deg, #16a34a, #0ea5e9);
    border-radius: 3px 0 0 3px;
  }
  .tor-val {
    font-weight: 700;
    font-size: 11px;
    text-align: right;
    /* 숫자 폭을 고정해 막대 오른쪽 값이 행마다 흔들리지 않게 한다 */
    font-variant-numeric: tabular-nums;
  }
  .tor-val.pos {
    color: var(--danger);
  }
  .tor-val.neg {
    color: var(--success);
  }
  @media (max-width: 760px) {
    .tor-row {
      grid-template-columns: 170px 1fr 84px;
    }
  }

  /* ── 종합분석 섹션 ── */
  .analysis-section {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 20px 24px;
    margin-bottom: 20px;
    box-shadow: var(--shadow);
  }
  .analysis-item {
    display: flex;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid #f1f5f9;
  }
  .analysis-item:last-child {
    border-bottom: none;
  }
  .analysis-num {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--primary);
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .analysis-content {
    flex: 1;
  }
  .analysis-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--primary);
    margin-bottom: 4px;
  }
  .analysis-desc {
    font-size: 12px;
    color: var(--text);
    line-height: 1.7;
  }
  .analysis-tag {
    display: inline-block;
    background: #e3f0ff; /* Lightblue-100 */
    color: #3b5db7; /* Lightblue-700 */
    border-radius: 4px;
    padding: 1px 7px;
    font-size: 11px;
    font-weight: 700;
    margin-right: 4px;
  }
  .analysis-tag.red {
    background: #fee6db; /* Error-100 */
    color: #b32542; /* Error-700 */
  }
  .analysis-tag.green {
    background: #dcfbea; /* Confirmed-100 */
    color: #00806a;
  }
  .analysis-tag.yellow {
    background: #fff8cd; /* Warning-Minor-100 */
    color: #b78803; /* Warning-Minor-700 */
  }

  /*
    ── ⑥ UT 항목별 분석결과 카드 ──
    UT 항목 하나당 카드 하나, 안에 "분석 결과" 입력칸이 들어간다.
    (기계 전용 영역이라 이 스코프의 클래스를 그대로 쓸 수 있다 —
     기계/전기가 공유하는 AnalysisOpinionSection만 인라인 style을 쓴다)
  */
  .anls-item {
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    background: var(--surface);
    padding: 12px 14px;
    margin-bottom: 10px;
  }
  .anls-item .anls-item-title {
    font-size: 13px;
    font-weight: 700;
    color: #135678; /* Primary-800 */
    margin-bottom: 8px;
  }
  .anls-item .anls-label {
    font-size: 11px;
    font-weight: 700;
    color: var(--text-muted);
    margin-bottom: 4px;
  }
  /* ⑥ 항목별 분석결과 / ⑦ 종합의견 입력칸 — 규격을 하나로 맞춘다 */
  .anls-item textarea,
  .opin-textarea {
    width: 100%;
    min-height: 60px;
    padding: 8px 10px;
    border: 1px solid var(--border-input);
    border-radius: var(--radius-lg);
    font-size: 12px;
    line-height: 1.7;
    font-family: inherit;
    resize: vertical;
  }
  .anls-item textarea:focus,
  .opin-textarea:focus {
    outline: none;
    border-color: var(--primary);
  }
  .opin-textarea {
    min-height: 90px;
  }

  /* ⑦ 종합의견 소제목 (저장 완료 표시 .saved-msg는 sharedStyles.ts에 있다) */
  .opin-label {
    font-size: 12px;
    font-weight: 700;
    margin: 16px 0 6px;
  }

  .status {
    border-radius: var(--radius);
    padding: 12px 16px;
    font-size: 13px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 16px;
  }
  .status-info {
    background: #e3f0ff; /* Lightblue-100 */
    border: 1px solid #adceff; /* Lightblue-300 */
    color: #3b5db7; /* Lightblue-700 */
  }

  .empty-state {
    text-align: center;
    padding: 40px 20px;
    color: var(--text-muted);
  }

  .spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: ut-spin 0.7s linear infinite;
  }
  @keyframes ut-spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* ── UT Matrix 선택 모달 ── */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .modal {
    width: 640px;
    max-width: calc(100vw - 48px);
    max-height: 90vh;
    background: #fff;
    border-radius: var(--radius);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .modal-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--border);
    background: var(--surface2);
  }
  .modal-header h3 {
    font-size: 14px;
    font-weight: 700;
    color: var(--primary);
  }
  .modal-header .btn-close {
    margin-left: auto;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 1px solid var(--border);
    border-radius: 2px;
    background: #fff;
    color: var(--text-muted);
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
  }
  .modal-body {
    flex: 1;
    overflow: auto;
    padding: 14px 16px;
  }
  .modal-footer {
    display: flex;
    gap: 8px;
    padding: 10px 16px;
    border-top: 1px solid var(--border);
    background: var(--surface2);
  }
  .modal-footer .right {
    margin-left: auto;
    display: flex;
    gap: 8px;
  }
  .prog-input {
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
  }
  .prog-input input {
    flex: 1;
    height: 32px;
    padding: 4px 8px;
    border: 1px solid var(--border-input);
    border-radius: var(--radius);
    font-size: 13px;
    font-family: inherit;
  }
  .prog-input input:focus {
    outline: none;
    border-color: var(--primary);
  }
  tr.row-clickable {
    cursor: pointer;
  }
  tr.row-clickable:hover {
    background: var(--surface2);
  }

  .badge-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--danger);
    color: #fff;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 700;
    padding: 1px 6px;
    margin-left: 4px;
  }

  .collapse-toggle {
    font-size: 12px;
    cursor: pointer;
    color: var(--accent);
    text-decoration: underline;
  }

  /* ── 헤더 정렬/필터 UI ── */
  thead th .th-label {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    user-select: none;
  }
  thead th .th-label:hover {
    text-decoration: underline;
  }
  thead th .sort-arrow {
    font-size: 9px;
    opacity: 0.5;
  }
  thead th .sort-arrow.active {
    opacity: 1;
    color: var(--primary);
  }
  thead th .th-filter-btn {
    cursor: pointer;
    margin-left: 6px;
    font-size: 10px;
    opacity: 0.55;
    user-select: none;
  }
  thead th .th-filter-btn:hover {
    opacity: 1;
  }
  thead th .th-filter-btn.active {
    color: #b75511; /* Warning-Major-700 (밝은 헤더 대비 확보) */
    opacity: 1;
  }

  .filter-dropdown {
    position: fixed;
    background: #fff;
    border: 1px solid var(--border-input); /* 레거시 팝업 보더 (Grey-400 #B9BCBB) */
    border-radius: 3px; /* 레거시 팝업 radius */
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.22), 0 7px 10px rgba(0, 0, 0, 0.12); /* 레거시 팝업 그림자 */
    z-index: 2000;
    max-height: 320px;
    overflow: auto;
    padding: 8px;
    font-size: 12px;
    min-width: 170px;
  }
  .filter-dropdown label {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 3px 5px;
    cursor: pointer;
    white-space: nowrap;
    border-radius: 4px;
    color: var(--text);
    font-weight: 400;
  }
  .filter-dropdown label:hover {
    background: #eeeaf8; /* PI 메뉴 hover/선택 (Secondary-50) */
  }
  .filter-dropdown .filter-actions {
    display: flex;
    gap: 6px;
    margin-bottom: 6px;
    padding-bottom: 6px;
    border-bottom: 1px solid var(--border);
  }
  .filter-dropdown .filter-actions button {
    flex: 1;
    font-size: 11px;
    padding: 4px 6px;
    border: 1px solid var(--border);
    background: #f7f9f8; /* Grey-50 */
    border-radius: 4px;
    cursor: pointer;
  }
  .filter-dropdown .filter-actions button:hover {
    background: #ebeeed; /* Grey-200 */
  }

  /* ── 세부 Summary 서브탭 ── */
  .summary-subtabs {
    display: flex;
    gap: 6px;
    margin-bottom: 12px;
  }

  /* ── 데이터 Q&A 챗봇 ── */
  .chat-toggle-btn {
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 54px;
    height: 54px;
    border-radius: 50%;
    background: var(--primary);
    color: #fff;
    font-size: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: var(--shadow-md);
    z-index: 3000;
    transition: transform 0.15s, background 0.15s;
  }
  .chat-toggle-btn:hover {
    transform: scale(1.07);
    background: var(--primary-light);
  }
  .chat-toggle-btn .badge-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--success);
    border: 2px solid #fff;
  }
  .chat-panel {
    position: fixed;
    bottom: 90px;
    right: 24px;
    width: 360px;
    height: 500px;
    background: #fff;
    border-radius: var(--radius);
    box-shadow: var(--shadow-md);
    display: flex;
    flex-direction: column;
    z-index: 3000;
    overflow: hidden;
    border: 1px solid var(--border);
  }
  .chat-header {
    background: var(--primary);
    color: #fff;
    padding: 12px 14px;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
  }
  .chat-header .chat-sub {
    font-size: 10px;
    font-weight: 400;
    opacity: 0.75;
    display: block;
    margin-top: 1px;
  }
  .chat-close {
    cursor: pointer;
    opacity: 0.8;
    font-size: 14px;
    padding: 2px 4px;
  }
  .chat-close:hover {
    opacity: 1;
  }
  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    background: var(--surface2);
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .chat-msg {
    max-width: 86%;
    padding: 8px 11px;
    font-size: 12px;
    line-height: 1.55;
    white-space: pre-line;
  }
  .chat-msg.user {
    align-self: flex-end;
    background: #135678; /* PI 챗봇 사용자 말풍선 (Primary-800) */
    color: #fff;
    border-radius: 12px 2px 12px 12px; /* PI 말풍선 규격 */
  }
  .chat-msg.bot {
    align-self: flex-start;
    background: #fff;
    border: 1px solid var(--border);
    color: var(--text);
    border-radius: 2px 12px 12px 12px; /* PI 말풍선 규격 */
  }
  .chat-quick {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    padding: 0 12px 8px;
    flex-shrink: 0;
  }
  .chat-quick button {
    font-size: 10.5px;
    padding: 4px 9px;
    border-radius: 14px;
    border: 1px solid var(--border);
    background: #fff;
    color: var(--text-muted);
    cursor: pointer;
    font-family: inherit;
  }
  .chat-quick button:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
  .chat-input-row {
    display: flex;
    border-top: 1px solid var(--border);
    flex-shrink: 0;
  }
  .chat-input-row input {
    flex: 1;
    border: none;
    padding: 11px 12px;
    font-size: 12px;
    font-family: inherit;
  }
  .chat-input-row input:focus {
    outline: none;
  }
  .chat-input-row button {
    border: none;
    background: var(--primary);
    color: #fff;
    padding: 0 16px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
  }
  .chat-input-row button:hover {
    background: var(--primary-light);
  }
`;
